import { Funil } from '@/types/funil';
import { ModeloProposta } from '@/types/modeloProposta';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';

// Tipos centralizados
export interface LeadPessoa {
    id: number;
    nome: string;
    email: string;
    telefone: string;
    empresa: string;
    origem: string;
    criadoEm: string;
    ultimoContato: string;
    status: 'ativo' | 'inativo';
    observacoes: string;
}

export interface Negocio {
    id: number;
    name: string;
    email: string;
    phone: string;
    company: string;
    boat: string;
    value: string;
    status: string;
    priority: string;
    source: string;
    createdAt: string;
    lastContact: string;
    notes: string;
    funnelId: number;
    salesRep: string;
    expectedCloseDate: string;
    customFields: {
        budgetApproved?: string;
        paymentMethod?: string;
    };
    tasks: Tarefa[];
    timeline: TimelineEntry[];
    leadOriginId?: number; // ID do lead que originou este negócio
}

export interface Tarefa {
    id: number;
    title: string;
    description?: string;
    type: string;
    status?: 'todo' | 'doing' | 'done';
    priority?: 'baixa' | 'media' | 'alta';
    dueDate: string;
    completed: boolean;
    assignedTo?: string;
    leadId?: number;
    negocioId?: number;
    createdAt?: string;
}

export interface TimelineEntry {
    id: number;
    type: 'call' | 'email' | 'proposal' | 'whatsapp' | 'status_change' | 'task' | 'meeting';
    title: string;
    description: string;
    timestamp: string;
    user: string;
    metadata?: {
        duration?: string;
        value?: string;
        previousStatus?: string;
        newStatus?: string;
        taskCompleted?: boolean;
    };
}

export interface Proposta {
    id: number;
    numero: string;
    negocioId?: number;
    cliente: {
        nome: string;
        email: string;
        telefone: string;
        empresa?: string;
    };
    produto: {
        tipo: 'embarcacao' | 'personalizado';
        embarcacaoId?: number;
        nome: string;
        descricao?: string;
        valor: string;
        especificacoes?: string;
        imagem?: string;
    };
    observacoes?: string;
    condicoesPagamento?: string;
    formaEntrega?: string;
    dataEmissao: string;
    validade: number;
    status: 'rascunho' | 'enviada' | 'aceita' | 'recusada';
}

// Funil agora está importado de @/types/funil

interface CRMContextType {
    // Leads
    leads: LeadPessoa[];
    addLead: (lead: Omit<LeadPessoa, 'id'>) => void;
    updateLead: (id: number, lead: Partial<LeadPessoa>) => void;
    deleteLead: (id: number) => void;
    convertLeadToNegocio: (leadId: number, funnelId: number) => Negocio;

    // Negócios
    negocios: Negocio[];
    addNegocio: (negocio: Omit<Negocio, 'id' | 'tasks' | 'timeline'>) => void;
    updateNegocio: (id: number, negocio: Partial<Negocio>) => void;
    deleteNegocio: (id: number) => void;
    moveNegocioToStatus: (id: number, newStatus: string) => void;
    addTimelineEntry: (negocioId: number, entry: Omit<TimelineEntry, 'id'>) => void;

    // Tarefas
    tarefas: Tarefa[];
    addTarefa: (tarefa: Omit<Tarefa, 'id'>) => void;
    updateTarefa: (id: number, tarefa: Partial<Tarefa>) => void;
    deleteTarefa: (id: number) => void;
    toggleTarefaCompletion: (id: number) => void;

    // Propostas
    propostas: Proposta[];
    addProposta: (proposta: Omit<Proposta, 'id' | 'numero' | 'dataEmissao'>) => void;
    updateProposta: (id: number, proposta: Partial<Proposta>) => void;
    deleteProposta: (id: number) => void;

    // Funis
    funis: Funil[];
    addFunil: (funil: Omit<Funil, 'id' | 'criadoEm' | 'bloqueado'>) => void;
    updateFunil: (id: number, funil: Partial<Funil>) => void;
    deleteFunil: (id: number) => void;

    // Modelos de Proposta
    modelosPropostas: ModeloProposta[];
    addModeloProposta: (modelo: Omit<ModeloProposta, 'id' | 'criadoEm'>) => void;
    updateModeloProposta: (id: string, modelo: Partial<ModeloProposta>) => void;
    deleteModeloProposta: (id: string) => void;
}

const CRMContext = createContext<CRMContextType | undefined>(undefined);

export const CRMProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // Estado inicial dos leads
    const [leads, setLeads] = useState<LeadPessoa[]>([
        {
            id: 1,
            nome: 'Carlos Silva',
            email: 'carlos@email.com',
            telefone: '(11) 99999-9999',
            empresa: 'Silva Transportes',
            origem: 'Website',
            criadoEm: '2024-01-15',
            ultimoContato: '2024-01-15',
            status: 'ativo',
            observacoes: 'Cliente muito interessado, tem experiência com embarcações',
        },
        {
            id: 2,
            nome: 'Maria Santos',
            email: 'maria@email.com',
            telefone: '(21) 88888-8888',
            empresa: 'Santos & Cia',
            origem: 'Indicação',
            criadoEm: '2024-01-14',
            ultimoContato: '2024-01-15',
            status: 'ativo',
            observacoes: 'Interessada em veleiros para lazer',
        },
    ]);

    // Estado inicial dos negócios
    const [negocios, setNegocios] = useState<Negocio[]>([
        {
            id: 1,
            name: 'Carlos Silva',
            email: 'carlos@email.com',
            phone: '(11) 99999-9999',
            company: 'Silva Transportes',
            boat: 'Lancha Sea Ray 290',
            value: 'R$ 450.000',
            status: 'novo',
            priority: 'alta',
            source: 'Website',
            createdAt: '2024-01-15',
            lastContact: '2024-01-15',
            notes: 'Cliente muito interessado, tem experiência com embarcações',
            funnelId: 1,
            salesRep: 'João Santos',
            expectedCloseDate: '2024-02-15',
            customFields: {
                budgetApproved: 'sim',
                paymentMethod: 'Financiamento',
            },
            tasks: [
                { id: 1, title: 'Ligar para apresentar opções', type: 'call', dueDate: '2024-01-16', completed: false },
                { id: 2, title: 'Enviar catálogo por email', type: 'email', dueDate: '2024-01-16', completed: true },
            ],
            timeline: [
                {
                    id: 1,
                    type: 'status_change',
                    title: 'Negócio criado',
                    description: 'Oportunidade iniciada a partir de lead do website',
                    timestamp: '2024-01-15 10:00',
                    user: 'Sistema',
                    metadata: { previousStatus: '-', newStatus: 'novo' },
                },
            ],
        },
    ]);

    // Estado inicial das tarefas
    const [tarefas, setTarefas] = useState<Tarefa[]>([
        {
            id: 1,
            title: 'Ligar para Carlos Silva',
            description: 'Apresentar opções de financiamento da Lancha Sea Ray 290',
            type: 'call',
            status: 'doing',
            priority: 'alta',
            dueDate: '2024-01-16',
            completed: false,
            assignedTo: 'João Santos',
            negocioId: 1,
            createdAt: '2024-01-15',
        },
        {
            id: 2,
            title: 'Enviar catálogo por email',
            description: 'Enviar catálogo de lanchas por email',
            type: 'email',
            status: 'done',
            priority: 'media',
            dueDate: '2024-01-16',
            completed: true,
            assignedTo: 'João Santos',
            negocioId: 1,
            createdAt: '2024-01-15',
        },
    ]);

    // Estado inicial das propostas
    const [propostas, setPropostas] = useState<Proposta[]>([
        {
            id: 1,
            numero: 'PROP-2025-001',
            negocioId: 1,
            cliente: {
                nome: 'João Silva',
                email: 'joao@example.com',
                telefone: '(11) 98765-4321',
                empresa: 'Silva Náutica',
            },
            produto: {
                tipo: 'embarcacao',
                nome: 'Lancha Fishing 265',
                valor: 'R$ 450.000,00',
                especificacoes: 'Motor 300HP, GPS, Sonar',
            },
            dataEmissao: '2025-01-15',
            validade: 30,
            status: 'enviada',
        },
    ]);

    // Estado inicial dos funis
    const [funis, setFunis] = useState<Funil[]>([
        {
            id: 1,
            nome: 'Venda Embarcação Usada',
            cor: '#3B82F6',
            criadoEm: '2025-01-01',
            bloqueado: true,
            etapas: [
                { id: 'novo', nome: 'Novo', cor: '#3B82F6', ordem: 0, tipo: 'inicio' },
                { id: 'interessado', nome: 'Interessado', cor: '#F59E0B', ordem: 1, tipo: 'meio' },
                { id: 'negociando', nome: 'Negociando', cor: '#F97316', ordem: 2, tipo: 'meio' },
                { id: 'proposta', nome: 'Proposta', cor: '#8B5CF6', ordem: 3, tipo: 'meio' },
                { id: 'fechado', nome: 'Fechado', cor: '#10B981', ordem: 4, tipo: 'ganho' },
                { id: 'perdido', nome: 'Perdido', cor: '#EF4444', ordem: 5, tipo: 'perdido' },
            ],
        },
        {
            id: 2,
            nome: 'Venda Embarcação Nova',
            cor: '#10B981',
            criadoEm: '2025-01-01',
            bloqueado: true,
            etapas: [
                { id: 'novo', nome: 'Novo', cor: '#3B82F6', ordem: 0, tipo: 'inicio' },
                { id: 'interessado', nome: 'Interessado', cor: '#F59E0B', ordem: 1, tipo: 'meio' },
                { id: 'negociando', nome: 'Negociando', cor: '#F97316', ordem: 2, tipo: 'meio' },
                { id: 'proposta', nome: 'Proposta', cor: '#8B5CF6', ordem: 3, tipo: 'meio' },
                { id: 'fechado', nome: 'Fechado', cor: '#10B981', ordem: 4, tipo: 'ganho' },
                { id: 'perdido', nome: 'Perdido', cor: '#EF4444', ordem: 5, tipo: 'perdido' },
            ],
        },
    ]);

    // Estado inicial dos modelos de proposta
    const [modelosPropostas, setModelosPropostas] = useState<ModeloProposta[]>([
        {
            id: '1',
            nome: 'Proposta Padrão',
            corPrimaria: '#0629AF',
            corSecundaria: '#10B981',
            nomeEmpresa: 'CompreNáutica',
            telefoneEmpresa: '(11) 98765-4321',
            emailEmpresa: 'contato@comprenautica.com.br',
            siteEmpresa: 'www.comprenautica.com.br',
            textoRodape: 'Ficamos à disposição para esclarecimentos adicionais.',
            textoValidade: 'Esta proposta é válida por {dias} dias a partir da data de emissão.',
            mostrarQRCode: false,
            padrao: true,
            criadoEm: '2025-01-01',
        },
    ]);

    // Sincronizar tarefas com negócios
    useEffect(() => {
        setNegocios((prev) =>
            prev.map((negocio) => ({
                ...negocio,
                tasks: tarefas.filter((t) => t.negocioId === negocio.id),
            })),
        );
    }, [tarefas]);

    // Funções de Leads
    const addLead = (lead: Omit<LeadPessoa, 'id'>) => {
        const newLead = { ...lead, id: Date.now() };
        setLeads((prev) => [...prev, newLead]);
    };

    const updateLead = (id: number, lead: Partial<LeadPessoa>) => {
        setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, ...lead } : l)));
    };

    const deleteLead = (id: number) => {
        setLeads((prev) => prev.filter((l) => l.id !== id));
    };

    const convertLeadToNegocio = (leadId: number, funnelId: number): Negocio => {
        const lead = leads.find((l) => l.id === leadId);
        if (!lead) throw new Error('Lead não encontrado');

        const today = new Date().toISOString().split('T')[0];
        const newNegocio: Negocio = {
            id: Date.now(),
            name: lead.nome,
            email: lead.email,
            phone: lead.telefone,
            company: lead.empresa,
            boat: '',
            value: '',
            status: 'novo',
            priority: 'media',
            source: lead.origem,
            createdAt: today,
            lastContact: today,
            notes: lead.observacoes,
            funnelId: funnelId,
            salesRep: '',
            expectedCloseDate: '',
            customFields: {},
            tasks: [],
            timeline: [
                {
                    id: Date.now(),
                    type: 'status_change',
                    title: 'Negócio criado a partir de lead',
                    description: `Convertido do lead ${lead.nome}`,
                    timestamp: new Date().toISOString(),
                    user: 'Sistema',
                    metadata: { previousStatus: 'lead', newStatus: 'novo' },
                },
            ],
            leadOriginId: leadId,
        };

        addNegocio(newNegocio);
        updateLead(leadId, { status: 'inativo' }); // Marcar lead como inativo após conversão
        return newNegocio;
    };

    // Funções de Negócios
    const addNegocio = (negocio: Omit<Negocio, 'id' | 'tasks' | 'timeline'>) => {
        const newNegocio: Negocio = {
            ...negocio,
            id: Date.now(),
            tasks: [],
            timeline: [
                {
                    id: Date.now(),
                    type: 'status_change',
                    title: 'Negócio criado',
                    description: 'Nova oportunidade registrada',
                    timestamp: new Date().toISOString(),
                    user: 'Sistema',
                    metadata: { previousStatus: '-', newStatus: negocio.status },
                },
            ],
        };
        setNegocios((prev) => [...prev, newNegocio]);
    };

    const updateNegocio = (id: number, negocio: Partial<Negocio>) => {
        setNegocios((prev) => prev.map((n) => (n.id === id ? { ...n, ...negocio } : n)));
    };

    const deleteNegocio = (id: number) => {
        setNegocios((prev) => prev.filter((n) => n.id !== id));
        // Remover tarefas associadas
        setTarefas((prev) => prev.filter((t) => t.negocioId !== id));
    };

    const moveNegocioToStatus = (id: number, newStatus: string) => {
        setNegocios((prev) =>
            prev.map((n) => {
                if (n.id === id) {
                    const timelineEntry: TimelineEntry = {
                        id: Date.now(),
                        type: 'status_change',
                        title: 'Status atualizado',
                        description: `Status alterado para ${newStatus}`,
                        timestamp: new Date().toISOString(),
                        user: 'Usuário',
                        metadata: { previousStatus: n.status, newStatus },
                    };
                    return {
                        ...n,
                        status: newStatus,
                        timeline: [...n.timeline, timelineEntry],
                    };
                }
                return n;
            }),
        );
    };

    const addTimelineEntry = (negocioId: number, entry: Omit<TimelineEntry, 'id'>) => {
        setNegocios((prev) =>
            prev.map((n) => {
                if (n.id === negocioId) {
                    return {
                        ...n,
                        timeline: [...n.timeline, { ...entry, id: Date.now() }],
                    };
                }
                return n;
            }),
        );
    };

    // Funções de Tarefas
    const addTarefa = (tarefa: Omit<Tarefa, 'id'>) => {
        const newTarefa: Tarefa = {
            ...tarefa,
            id: Date.now(),
            completed: tarefa.completed ?? false,
            status: tarefa.status || 'todo',
            priority: tarefa.priority || 'media',
            createdAt: tarefa.createdAt || new Date().toISOString().split('T')[0],
        };
        setTarefas((prev) => [...prev, newTarefa]);

        // Adicionar à timeline do negócio se aplicável
        if (tarefa.negocioId) {
            addTimelineEntry(tarefa.negocioId, {
                type: 'task',
                title: 'Nova tarefa criada',
                description: tarefa.title,
                timestamp: new Date().toISOString(),
                user: 'Usuário',
            });
        }
    };

    const updateTarefa = (id: number, tarefa: Partial<Tarefa>) => {
        setTarefas((prev) => prev.map((t) => (t.id === id ? { ...t, ...tarefa } : t)));
    };

    const deleteTarefa = (id: number) => {
        setTarefas((prev) => prev.filter((t) => t.id !== id));
    };

    const toggleTarefaCompletion = (id: number) => {
        setTarefas((prev) =>
            prev.map((t) => {
                if (t.id === id) {
                    const completed = !t.completed;
                    const status = completed ? 'done' : 'todo';

                    // Adicionar à timeline do negócio
                    if (t.negocioId) {
                        addTimelineEntry(t.negocioId, {
                            type: 'task',
                            title: completed ? 'Tarefa concluída' : 'Tarefa reaberta',
                            description: t.title,
                            timestamp: new Date().toISOString(),
                            user: 'Usuário',
                            metadata: { taskCompleted: completed },
                        });
                    }

                    return { ...t, completed, status };
                }
                return t;
            }),
        );
    };

    // Funções de Propostas
    const addProposta = (proposta: Omit<Proposta, 'id' | 'numero' | 'dataEmissao'>) => {
        const newProposta: Proposta = {
            ...proposta,
            id: Date.now(),
            numero: `PROP-2025-${String(propostas.length + 1).padStart(3, '0')}`,
            dataEmissao: new Date().toISOString().split('T')[0],
        };
        setPropostas((prev) => [...prev, newProposta]);

        // Adicionar à timeline do negócio se aplicável
        if (proposta.negocioId) {
            addTimelineEntry(proposta.negocioId, {
                type: 'proposal',
                title: 'Proposta gerada',
                description: `Proposta ${newProposta.numero} criada`,
                timestamp: new Date().toISOString(),
                user: 'Usuário',
                metadata: { value: proposta.produto.valor },
            });
        }
    };

    const updateProposta = (id: number, proposta: Partial<Proposta>) => {
        setPropostas((prev) => prev.map((p) => (p.id === id ? { ...p, ...proposta } : p)));
    };

    const deleteProposta = (id: number) => {
        setPropostas((prev) => prev.filter((p) => p.id !== id));
    };

    // Funções de Funis
    const addFunil = (funil: Omit<Funil, 'id' | 'criadoEm' | 'bloqueado'>) => {
        const newFunil: Funil = {
            ...funil,
            id: Date.now(),
            criadoEm: new Date().toISOString(),
            bloqueado: true, // Imediatamente bloqueado após criação
        };
        setFunis((prev) => [...prev, newFunil]);
    };

    const updateFunil = (id: number, funil: Partial<Funil>) => {
        setFunis((prev) => prev.map((f) => (f.id === id ? { ...f, ...funil } : f)));
    };

    const deleteFunil = (id: number) => {
        setFunis((prev) => prev.filter((f) => f.id !== id));
    };

    // Funções de Modelos de Proposta
    const addModeloProposta = (modelo: Omit<ModeloProposta, 'id' | 'criadoEm'>) => {
        const newModelo: ModeloProposta = {
            ...modelo,
            id: Date.now().toString(),
            criadoEm: new Date().toISOString(),
        };

        // Se o novo modelo for padrão, remover flag de outros
        if (modelo.padrao) {
            setModelosPropostas((prev) => [...prev.map((m) => ({ ...m, padrao: false })), newModelo]);
        } else {
            setModelosPropostas((prev) => [...prev, newModelo]);
        }
    };

    const updateModeloProposta = (id: string, modelo: Partial<ModeloProposta>) => {
        // Se estiver definindo como padrão, remover flag de outros
        if (modelo.padrao) {
            setModelosPropostas((prev) =>
                prev.map((m) => ({
                    ...m,
                    padrao: m.id === id ? true : false,
                    ...(m.id === id ? modelo : {}),
                })),
            );
        } else {
            setModelosPropostas((prev) => prev.map((m) => (m.id === id ? { ...m, ...modelo } : m)));
        }
    };

    const deleteModeloProposta = (id: string) => {
        setModelosPropostas((prev) => prev.filter((m) => m.id !== id));
    };

    const value: CRMContextType = {
        leads,
        addLead,
        updateLead,
        deleteLead,
        convertLeadToNegocio,
        negocios,
        addNegocio,
        updateNegocio,
        deleteNegocio,
        moveNegocioToStatus,
        addTimelineEntry,
        tarefas,
        addTarefa,
        updateTarefa,
        deleteTarefa,
        toggleTarefaCompletion,
        propostas,
        addProposta,
        updateProposta,
        deleteProposta,
        funis,
        addFunil,
        updateFunil,
        deleteFunil,
        modelosPropostas,
        addModeloProposta,
        updateModeloProposta,
        deleteModeloProposta,
    };

    return <CRMContext.Provider value={value}>{children}</CRMContext.Provider>;
};

export const useCRM = () => {
    const context = useContext(CRMContext);
    if (!context) {
        throw new Error('useCRM must be used within a CRMProvider');
    }
    return context;
};
